import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { DownloadIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
const DisputeReports: React.FC = () => {
  // Mock Data
  const reasonCodeData = [{
    code: '4837 - Fraud',
    count: 145
  }, {
    code: '4853 - Not as Described',
    count: 85
  }, {
    code: '4855 - Goods Not Received',
    count: 110
  }, {
    code: '4834 - Duplicate Processing',
    count: 40
  }, {
    code: '4840 - Fraudulent Processing',
    count: 65
  }];
  const merchantRiskData = [{
    merchant: 'TechStore PK',
    ratio: '2.4%',
    disputes: 45,
    volume: 'PKR 12.5M',
    risk: 'High'
  }, {
    merchant: 'Travel Agency',
    ratio: '1.8%',
    disputes: 32,
    volume: 'PKR 45.2M',
    risk: 'Medium'
  }, {
    merchant: 'Fashion Hub',
    ratio: '0.9%',
    disputes: 18,
    volume: 'PKR 8.4M',
    risk: 'Low'
  }, {
    merchant: 'Electro World',
    ratio: '3.1%',
    disputes: 55,
    volume: 'PKR 18.9M',
    risk: 'High'
  }, {
    merchant: 'Grocery Plus',
    ratio: '0.2%',
    disputes: 5,
    volume: 'PKR 5.2M',
    risk: 'Low'
  }];
  const columns = [{
    header: 'Merchant',
    accessor: 'merchant',
    className: 'font-medium text-gray-900 dark:text-white'
  }, {
    header: 'Dispute Ratio',
    accessor: 'ratio',
    className: 'font-semibold'
  }, {
    header: 'Total Disputes',
    accessor: 'disputes'
  }, {
    header: 'Transaction Volume',
    accessor: 'volume'
  }, {
    header: 'Risk Level',
    accessor: (row: any) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.risk === 'High' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : row.risk === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'}`} data-id="element-4080">
          {row.risk}
        </span>
  }];
  return <div className="space-y-6" data-id="element-4081">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-4082">
        <div data-id="element-4083">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4084">
            Dispute Analytics & Reporting
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4085">
            Analyze dispute trends, reason codes, and merchant risk profiles.
          </p>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4086">
          <DownloadIcon className="h-4 w-4 mr-2" data-id="element-4087" />
          Export Full Report
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4088">
        <Card title="Disputes by Reason Code (YTD)" data-id="element-4089">
          <div className="h-80" data-id="element-4090">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4091">
              <BarChart data={reasonCodeData} layout="vertical" margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-4092">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#374151" opacity={0.2} data-id="element-4093" />
                <XAxis type="number" axisLine={false} tickLine={false} data-id="element-4094" />
                <YAxis dataKey="code" type="category" axisLine={false} tickLine={false} width={150} data-id="element-4095" />
                <Tooltip cursor={{
                fill: 'transparent'
              }} data-id="element-4096" />
                <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={24} data-id="element-4097" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Financial Exposure Trend (PKR Millions)" data-id="element-4098">
          <div className="h-80" data-id="element-4099">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4100">
              <LineChart data={[{
              month: 'Jan',
              exposure: 2.1
            }, {
              month: 'Feb',
              exposure: 2.4
            }, {
              month: 'Mar',
              exposure: 3.2
            }, {
              month: 'Apr',
              exposure: 2.8
            }, {
              month: 'May',
              exposure: 3.9
            }, {
              month: 'Jun',
              exposure: 4.2
            }]} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-4101">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.2} data-id="element-4102" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} data-id="element-4103" />
                <YAxis axisLine={false} tickLine={false} data-id="element-4104" />
                <Tooltip data-id="element-4105" />
                <Line type="monotone" dataKey="exposure" stroke="#f59e0b" strokeWidth={3} dot={{
                r: 4
              }} activeDot={{
                r: 6
              }} data-id="element-4106" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card title="High-Risk Merchants (Dispute Ratio > 1%)" data-id="element-4107">
        <Table columns={columns} data={merchantRiskData} keyField="merchant" pagination={false} data-id="element-4108" />
      </Card>
    </div>;
};
export default DisputeReports;